import React, { useState } from 'react';
import ItemThumbnail from '../../../../components/video/ItemThumbnail';
import { A } from '@osmosis/tailwind-ui/components/Text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { H4 } from '@osmosis/tailwind-ui/components/Text';
import PlaylistIcon from '../../../../components/icons/PlaylistIcon';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlaylistSearchHitType } from '../../../../types/search';
import Snackbar from '../../../../components/Snackbar';
import useModal from '../../../../hooks/useModal';
import { formatSecondsToDuration } from '@osmosis/shared/video';
import { PROD_MEDIA_SERVER } from '@osmosis/constants';
import { PlaylistLearnNodeItem } from '@osmosis/shared/types/playlist';
import LockIcon from '../../../../components/search/LockIcon';
import { useUserContext } from '../../../../context/UserContext';
import { SubProduct } from '@elsevier-health/osmosis-shared/types/user';
import BreadcrumbsV2 from '../../../../components/BreadcrumbsV2/BreadcrumbsV2';
import { text4 } from '@osmosis/tailwind-ui/styles/text';

type Props = {
  inPlaylist: boolean;
  video: PlaylistSearchHitType;
  onAdd: (items: PlaylistLearnNodeItem[]) => Promise<void>;
  onReplace: (items: PlaylistLearnNodeItem[]) => Promise<void>;
  toBeRetiredTag: { tagID: number; tag: string } | undefined;
};

const mockVideo = {
  learnNodeID: 1,
  thumb:
    'https://osmose-it.s3.amazonaws.com/r1bZ52zLQVi59r05f26FHlgZQbCOoB6H/_.jpg',
  videoDurationSec: '719',
  concept: 'Introduction to the cardiovascular system',
  url: '/learn/Introduction_to_the_cardiovascular_system?from=/md/foundational-sciences/anatomy/introduction-to-anatomy',
  breadcrumbsv2: [],
  subProducts: [
    {
      groupName: 'Basic Sciences',
      state: 'active',
    },
    {
      groupName: 'Clinical Sciences',
      state: 'locked',
    },
  ],
} as unknown as PlaylistSearchHitType;

/**
 * Represents item in the playlist search.
 * InPlaylistVideo - in playlist video state.
 * Video - gets video from parent, working with mock data yet.
 */
const PlaylistSearchRow = (props: Props) => {
  const {
    inPlaylist,
    video = mockVideo,
    onAdd,
    toBeRetiredTag,
    onReplace,
  } = props;
  const { formatMessage } = useIntl();

  const { subProducts } = video;
  const { user } = useUserContext();
  const isLockedFlag = user?.hasLockedContentFlag;

  const [isButtonHover, setIsButtonHover] = useState(false);
  const { hide, isVisible } = useModal();

  let lockedSubProduct;
  if (subProducts && subProducts.length === 1) {
    lockedSubProduct = subProducts
      .find((sp) => sp.state === 'locked')
      ?.groupName.replace(/ /g, '_')
      .toLowerCase();
  }

  const { breadcrumbsv2 } = video;
  const linkClassName = 'text-md';
  const playIcon = video.category === 'video';

  return (
    <article>
      <Flex
        className="bg-0 flex justify-between px-2 py-3 hover:bg-gray-50"
        onMouseOver={() => setIsButtonHover(true)}
        onMouseLeave={() => setIsButtonHover(false)}
        data-testid="playlist-search-row-item"
      >
        <Flex className="relative h-14 w-24" tabIndex={-1}>
          <A href={video.url} tabIndex={-1}>
            <ItemThumbnail
              thumbnailURL={`${PROD_MEDIA_SERVER}${video.thumb}`}
              playIcon={playIcon}
              showDocIcon={!playIcon}
              iconLabel={playIcon ? 'Video' : 'High-yield notes'}
              altText=""
              title={video.concept}
              minHeight="55px"
              duration={formatSecondsToDuration(+video.videoDurationSec)}
            />
          </A>
        </Flex>
        <Box className="ml-3 flex-1">
          <H4 className={`inline ${text4} opacity-75`}>
            <A
              className="text-primary !font-bold no-underline hover:text-blue-600"
              href={video.url}
              data-testid="playlist-search-title"
            >
              {video.concept}
            </A>
            {lockedSubProduct && isLockedFlag ? (
              <LockIcon variant={lockedSubProduct} />
            ) : null}
          </H4>

          {breadcrumbsv2?.length ? (
            <Box>
              <BreadcrumbsV2
                breadcrumb={breadcrumbsv2[0] || null}
                linkClassName={linkClassName}
                // onBreadCrumbItemClick={onBreadCrumbItemClick}
              />
            </Box>
          ) : null}
        </Box>
        {!toBeRetiredTag && (
          <Button
            className={`ml-3 h-8 rounded-2xl px-4 shadow-slate-500 hover:bg-gray-50 ${
              !inPlaylist
                ? 'hover:shadow-flat hover:bg-blue-600 hover:text-white'
                : ''
            } ${!inPlaylist && isButtonHover ? 'bg-blue-600 text-white' : ''}`}
            data-testid="playlist-search-add"
            aria-label={formatMessage(
              {
                id: 'playlist-search-add',
                defaultMessage: 'Add "{name}" to playlist',
              },
              { name: video.concept },
            )}
            startIcon={
              !localStorage.getItem('playlistItem') ? (
                <PlaylistIcon
                  fill={!inPlaylist && isButtonHover ? 'textInvert' : 'text'}
                  opacity={isButtonHover ? 1 : 0.75}
                />
              ) : null
            }
            disabled={inPlaylist}
            variant="outline"
            onMouseOver={() => setIsButtonHover(true)}
            onMouseLeave={() => setIsButtonHover(false)}
            onClick={() => {
              const playlistNodeItem = {
                learnNodeID: video.learnNodeID,
                name: video.concept,
                subProducts: video.subProducts as SubProduct[],
                addQuizData: true,
              };
              localStorage.getItem('playlistItem')
                ? onReplace([playlistNodeItem])
                : onAdd([playlistNodeItem]);
            }}
          >
            {!inPlaylist &&
              (localStorage.getItem('playlistItem') ? (
                <FormattedMessage
                  id="playlist-search-replace"
                  defaultMessage="Replace"
                />
              ) : (
                <FormattedMessage
                  id="playlist-search-add"
                  defaultMessage="Add"
                />
              ))}
            {inPlaylist &&
              (localStorage.getItem('playlistItem') ? (
                <FormattedMessage
                  id="playlist-search-replaced"
                  defaultMessage="Replaced"
                />
              ) : (
                <FormattedMessage
                  id="playlist-search-added"
                  defaultMessage="Added"
                />
              ))}
          </Button>
        )}
        <Snackbar
          isVisible={isVisible}
          hide={hide}
          message={`"${video.concept}" added successfully!`}
          variant="bg-gray-500"
          motionStyle={{ maxWidth: '800px' }}
        />
      </Flex>
    </article>
  );
};

export default PlaylistSearchRow;
